import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowLeft } from "lucide-react";

const usePageMeta = (title, description) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    const prev = m ? m.getAttribute("content") : null;
    if (m && description) m.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (m && prev != null) m.setAttribute("content", prev);
    };
  }, [title, description]);
};

const Shell = ({ title, effectiveDate, metaTitle, metaDescription, children, testid }) => {
  usePageMeta(metaTitle || `${title} | Cellblock B`, metaDescription || "");
  return (
  <div className="App relative min-h-screen" data-testid={testid}>
    {/* Slim header */}
    <header className="border-b" style={{ borderColor: "var(--line)", background: "rgba(8,8,10,0.85)", backdropFilter: "blur(8px)" }}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="legal-back-home">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: "var(--accent)", color: "#0b0b0c" }}>
            <Lock size={18} strokeWidth={2.5} />
          </div>
          <div className="font-display text-xl">CELLBLOCK B</div>
        </Link>
        <Link to="/" className="text-sm flex items-center gap-1.5 hover:text-white transition" style={{ color: "var(--muted)" }}>
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
    </header>

    {/* Body */}
    <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
      <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--accent)" }}>
        Legal
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">{title}</h1>
      <div className="mt-3 text-sm font-mono" style={{ color: "var(--muted)" }}>
        Effective Date: {effectiveDate}
      </div>
      <div
        className="legal-body mt-10 space-y-6 text-base leading-relaxed"
        style={{ color: "rgba(255,255,255,0.85)" }}
      >
        {children}
      </div>
    </main>

    {/* Tiny footer */}
    <div className="border-t" style={{ borderColor: "var(--line)" }}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-xs" style={{ color: "var(--muted)" }}>
        <div>© {new Date().getFullYear()} Cellblock B. All Rights Reserved.</div>
        <div className="flex flex-wrap gap-4">
          <Link to="/privacy-policy" className="hover:text-white transition">Privacy Policy</Link>
          <Link to="/refund-policy" className="hover:text-white transition">Refund Policy</Link>
          <Link to="/terms-and-conditions" className="hover:text-white transition">Terms &amp; Conditions</Link>
        </div>
      </div>
    </div>
  </div>
  );
};

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl sm:text-3xl mt-10 mb-2 text-white">{children}</h2>
);

const P = ({ children }) => <p>{children}</p>;

const UL = ({ children }) => (
  <ul className="list-disc pl-6 space-y-1.5" style={{ color: "rgba(255,255,255,0.85)" }}>
    {children}
  </ul>
);

const StripeAU = () => (
  <a
    href="https://stripe.com/au"
    target="_blank"
    rel="noopener noreferrer"
    className="underline hover:text-white transition"
    style={{ color: "var(--accent)" }}
    data-testid="legal-stripe-au-link"
  >
    Stripe Australia
  </a>
);

const ContactLink = () => (
  <Link
    to="/#contact"
    className="underline hover:text-white transition"
    style={{ color: "var(--accent)" }}
    data-testid="legal-contact-link"
  >
    CellBlockB
  </Link>
);

// ============= Terms & Conditions =============
export function TermsAndConditionsPage() {
  return (
    <Shell title="Terms and Conditions" effectiveDate="21 May 2026" testid="terms-page"
      metaTitle="Terms & Conditions | Cellblock B"
      metaDescription="Cellblock B Terms and Conditions for our Australian prison call number service. Subscription, refund, and limitation of liability terms.">
      <H2>Introduction</H2>
      <P>Welcome to CellBlockB.</P>
      <P>These Terms and Conditions outline the rules for using our website, mobile application, and services.</P>
      <P>By accessing or using our services, you agree to comply with these Terms.</P>

      <H2>Services</H2>
      <P>
        CellBlockB provides communication services that allow users to receive approved inmate calls through
        local landline numbers connected via our mobile application and related systems.
      </P>

      <H2>User Responsibilities</H2>
      <P>Users agree to:</P>
      <UL>
        <li>Comply with all applicable laws and correctional facility rules;</li>
        <li>Keep account information secure and confidential;</li>
        <li>Use the service only for lawful purposes.</li>
      </UL>
      <P>Users must not use the service for unlawful, abusive, or prohibited activities.</P>

      <H2>Subscription and Billing</H2>
      <P>
        Our services may be provided on a subscription basis, with payments securely processed through <StripeAU />.
      </P>
      <P>By purchasing a subscription, you agree that:</P>
      <UL>
        <li>Fees are charged in Australian Dollars (AUD);</li>
        <li>Subscription payments may recur automatically;</li>
        <li>Billing information must remain current and valid.</li>
      </UL>
      <P>Refunds are subject to our Refund Policy and applicable Australian Consumer Law.</P>

      <H2>Limitation of Liability</H2>
      <P>To the maximum extent permitted by law, CellBlockB is not liable for damages arising from:</P>
      <UL>
        <li>Service interruptions;</li>
        <li>Technical issues;</li>
        <li>Inability to access or use the service.</li>
      </UL>
      <P>Nothing in these Terms excludes rights under Australian Consumer Law.</P>

      <H2>Changes to Terms</H2>
      <P>
        CellBlockB may update these Terms and Conditions at any time. Updated versions will be posted on our
        website, and continued use of the service constitutes acceptance of the updated Terms.
      </P>

      <H2>Contact Information</H2>
      <P>For support or enquiries, please contact:</P>
      <P><ContactLink /></P>
    </Shell>
  );
}

// ============= Privacy Policy =============
export function PrivacyPolicyPage() {
  return (
    <Shell title="Privacy Policy" effectiveDate="21 May 2026" testid="privacy-page"
      metaTitle="Privacy Policy | Cellblock B"
      metaDescription="How Cellblock B collects, uses, and protects your personal information. Payment data processed securely through Stripe Australia.">
      <H2>Introduction</H2>
      <P>
        CellBlockB is committed to protecting your privacy. This Privacy Policy explains how we collect, use,
        and protect your personal information.
      </P>

      <H2>Information We Collect</H2>
      <P>We may collect:</P>
      <UL>
        <li>Your name;</li>
        <li>Email address;</li>
        <li>Phone number;</li>
        <li>Payment information processed securely through <StripeAU />.</li>
      </UL>

      <H2>How We Use Your Information</H2>
      <P>We use your information to:</P>
      <UL>
        <li>Provide and improve our services;</li>
        <li>Process payments and manage subscriptions;</li>
        <li>Communicate with you about your account and support requests.</li>
      </UL>

      <H2>Sharing Your Information</H2>
      <P>We do not sell or rent your personal information.</P>
      <P>Payment information may be shared securely with Stripe for payment processing purposes only.</P>

      <H2>Security</H2>
      <P>
        We take all necessary steps to protect your personal information from unauthorised access, misuse, or
        disclosure.
      </P>

      <H2>Changes to This Policy</H2>
      <P>We may update this Privacy Policy from time to time. Any updates will be posted on our website.</P>

      <H2>Contact Information</H2>
      <P>If you have any questions about this Privacy Policy, please contact:</P>
      <P><ContactLink /></P>
    </Shell>
  );
}

// ============= Refund Policy =============
export function RefundPolicyPage() {
  return (
    <Shell title="Refund Policy" effectiveDate="21 May 2026" testid="refund-page">
      <H2>10-Day Money-Back Guarantee</H2>
      <P>At CellBlockB, we want you to feel confident using our service.</P>
      <P>
        If you are not satisfied, you may request a refund within 10 days of your number being activated.
      </P>
      <P>Simply contact our support team with your account details and we will assist you.</P>
      <P>
        If there is a technical issue with the service, we will work to resolve it as quickly as possible and
        if we are unable to remedy the issue we will provide you with either:
      </P>
      <UL>
        <li>A replacement number;</li>
        <li>A partial refund; or</li>
        <li>A full refund where appropriate.</li>
      </UL>

      <H2>Prison Transfers</H2>
      <P>
        If an inmate is transferred to another correctional facility, we may provide a replacement number
        where available.
      </P>

      <H2>Australian Consumer Law</H2>
      <P>Nothing in this policy limits your rights under Australian Consumer Law.</P>

      <H2>Contact</H2>
      <P>For refund requests or support, please contact:</P>
      <P><ContactLink /></P>
    </Shell>
  );
}
